import React, { useCallback } from 'react';
import { ArrowRight, BarChart2, Save, RefreshCw, History, Star } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент основной формы конвертера расстояний
 */
const ConversionForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isConverting, 
  setIsConverting,
  setResult,
  saveToHistory,
  addToFavorites,
  unitDefinitions
}) => {
  
  // Обработчик изменения полей ввода
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const { fromValue } = formData;
    
    // Проверяем, что значение - число
    const numValue = parseFloatOrDefault(fromValue, null);
    if (numValue === null) {
      newErrors.fromValue = 'Введите корректное число';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);
  
  // Обмен единицами измерения
  const swapUnits = () => {
    setFormData(prev => ({
      ...prev,
      fromUnit: prev.toUnit,
      toUnit: prev.fromUnit
    }));
  };
  
  // Сброс формы
  const resetForm = () => {
    setFormData({
      fromValue: '1',
      fromUnit: 'km',
      toUnit: 'm',
      precision: '2'
    });
    setResult(null);
    setErrors({});
  };
  
  // Функция конвертации
  const convertUnits = useCallback(() => {
    if (!validateForm()) {
      return;
    }
    
    setIsConverting(true);
    
    // Используем setTimeout для обновления UI перед конвертацией
    setTimeout(() => {
      try {
        const { fromValue, fromUnit, toUnit, precision } = formData;
        
        // Получаем коэффициенты конвертации из определений единиц
        const fromUnitDef = unitDefinitions[fromUnit];
        const toUnitDef = unitDefinitions[toUnit];
        
        if (!fromUnitDef || !toUnitDef) {
          throw new Error('Неизвестная единица измерения');
        }
        
        // Преобразование в базовые единицы (метры), затем в целевую единицу
        const numFromValue = parseFloatOrDefault(fromValue);
        const valueInMeters = numFromValue * fromUnitDef.baseConversion;
        const convertedValue = valueInMeters / toUnitDef.baseConversion;
        
        // Округляем до указанной точности
        const precisionNum = parseInt(precision, 10) || 2;
        const roundedValue = parseFloat(convertedValue.toFixed(precisionNum));
        
        // Создаем объект результата
        const resultObj = {
          fromValue: numFromValue,
          fromUnit,
          toValue: roundedValue,
          toUnit,
          formula: `${numFromValue} ${fromUnitDef.shortName} = ${roundedValue} ${toUnitDef.shortName}`
        };
        
        // Устанавливаем результат
        setResult(resultObj);
        
        // Сохраняем в историю
        saveToHistory(resultObj);
        
      } catch (error) {
        console.error("Ошибка конвертации:", error);
        setErrors({ general: `Произошла ошибка: ${error.message}. Пожалуйста, проверьте введенные данные.` });
        setResult(null);
      } finally {
        setIsConverting(false);
      }
    }, 50);
  }, [formData, validateForm, unitDefinitions, saveToHistory, setResult, setErrors, setIsConverting]);
  
  // Добавление текущей конвертации в избранное
  const handleAddToFavorites = () => {
    if (validateForm()) {
      addToFavorites(formData);
    }
  };
  
  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    convertUnits();
  };
  
  // Группируем единицы измерения по системам для селектов
  const groupedUnits = {
    'Метрическая система': Object.values(unitDefinitions).filter(unit => unit.system === 'metric'),
    'Имперская система': Object.values(unitDefinitions).filter(unit => unit.system === 'imperial'),
    'Морские единицы': Object.values(unitDefinitions).filter(unit => unit.system === 'nautical'),
    'Астрономические единицы': Object.values(unitDefinitions).filter(unit => unit.system === 'astronomical'),
    'Русские исторические': Object.values(unitDefinitions).filter(unit => unit.system === 'russian'),
    'Современные российские': Object.values(unitDefinitions).filter(unit => unit.system === 'modern_russian')
  };
  
  return (
    <form onSubmit={handleSubmit} noValidate className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <BarChart2 className="w-6 h-6 text-purple-500" />
        Конвертация расстояний
      </h2>
      
      {/* Общая ошибка */}
      {errors.general && (
        <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
          {errors.general}
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-8 gap-4 mb-6 items-end">
        {/* Значение для конвертации */}
        <div className="md:col-span-2">
          <label htmlFor="fromValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Значение
          </label>
          <input
            type="text"
            id="fromValue"
            name="fromValue"
            value={formData.fromValue}
            onChange={handleInputChange}
            className={`form-input w-full ${errors.fromValue ? 'input-error' : ''}`}
            placeholder="Введите число"
          />
          {errors.fromValue && (
            <p className="mt-1 text-xs text-red-500 dark:text-red-400">
              {errors.fromValue}
            </p>
          )}
        </div>
        
        {/* Исходная единица измерения */}
        <div className="md:col-span-2">
          <label htmlFor="fromUnit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Из
          </label>
          <select
            id="fromUnit"
            name="fromUnit"
            value={formData.fromUnit}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {Object.entries(groupedUnits).map(([group, units]) => {
              if (units.length === 0) return null;
              return (
                <optgroup key={group} label={group}>
                  {units.map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name} ({unit.shortName})
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
        
        {/* Кнопка для обмена единицами */}
        <div className="md:col-span-1 flex justify-center">
          <button
            type="button"
            onClick={swapUnits}
            className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-full hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
            aria-label="Поменять единицы местами"
            title="Поменять единицы местами"
          >
            <ArrowRight className="w-5 h-5 text-purple-600 dark:text-purple-400" />
          </button>
        </div>
        
        {/* Целевая единица измерения */}
        <div className="md:col-span-2">
          <label htmlFor="toUnit" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            В
          </label>
          <select
            id="toUnit"
            name="toUnit"
            value={formData.toUnit}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            {Object.entries(groupedUnits).map(([group, units]) => {
              if (units.length === 0) return null;
              return (
                <optgroup key={group} label={group}>
                  {units.map(unit => (
                    <option key={unit.id} value={unit.id}>
                      {unit.name} ({unit.shortName})
                    </option>
                  ))}
                </optgroup>
              );
            })}
          </select>
        </div>
        
        {/* Точность округления */}
        <div className="md:col-span-1">
          <label htmlFor="precision" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Точность
          </label>
          <select
            id="precision"
            name="precision"
            value={formData.precision}
            onChange={handleInputChange}
            className="form-select w-full"
          >
            <option value="0">0 знаков</option>
            <option value="1">1 знак</option>
            <option value="2">2 знака</option>
            <option value="3">3 знака</option>
            <option value="4">4 знака</option>
            <option value="5">5 знаков</option>
            <option value="6">6 знаков</option>
          </select>
        </div>
      </div>
      
      {/* Кнопки действий */}
      <div className="mt-6 flex flex-wrap gap-4">
        <Button
          type="submit"
          primary
          icon={<BarChart2 className="w-5 h-5" />}
          disabled={isConverting || Object.keys(errors).length > 0}
          isLoading={isConverting}
          size="lg"
        >
          {isConverting ? 'Конвертируем...' : 'Конвертировать'}
        </Button>
        
        <Button
          type="button"
          onClick={resetForm}
          icon={<RefreshCw className="w-5 h-5" />}
          variant="secondary"
          disabled={isConverting}
          size="lg"
        >
          Сбросить
        </Button>
        
        <Button
          type="button"
          onClick={handleAddToFavorites}
          icon={<Star className="w-5 h-5" />}
          variant="secondary"
          disabled={isConverting}
          size="lg"
        >
          В избранное
        </Button>
      </div>
      
      {Object.keys(errors).length > 0 && !errors.general && (
        <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
          Пожалуйста, исправьте ошибки в форме.
        </p>
      )}
      
      <div className="mt-6 p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-100 dark:border-purple-800">
        <div className="flex items-start gap-3">
          <History className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-purple-800 dark:text-purple-300 mb-1">
              Полезный совет
            </h3>
            <p className="text-sm text-purple-700 dark:text-purple-400">
              В России исторически использовались такие единицы как верста, сажень, аршин и вершок. Московская кольцевая автодорога (МКАД) имеет длину около 108,9 км и иногда используется россиянами как неформальная единица измерения.
            </p>
          </div>
        </div>
      </div>
    </form>
  );
};

export default ConversionForm;